import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  CardMedia,
} from "@mui/material";
import Button from "../../../common/Button";
import { getRole } from "../../../../helpers/auth";
import { roles } from "../../../../constants/routes";
import { useOrganisation } from "../../../../hooks/useOrganisation";
import ArticleResources from "../../Dashboard/ArticleDetails/ArticleResource";
import { IMAGE_URL_AZURE } from "../../../../constants/env";

const ContentPreview = ({
  setStep,
  step,
  content,
  image,
  title,
  topics,
  scheduledDate,
  handleAddContent,
  modal,
  isLoadAddContent,
  publish,
  files,
  isEdit,
  status,
}) => {
  const role = getRole();
  const { primaryColor } = useOrganisation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [processedContent, setProcessedContent] = useState(content);

  // Function to process HTML content and resize media elements
  useEffect(() => {
    if (content) {
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;

      // Find all images in the content
      const images = tempDiv.querySelectorAll("img");
      images.forEach((img) => {
        // Set responsive image styles
        img.style.maxWidth = "100%";
        img.style.height = "auto";
        img.style.display = "block";
        img.style.margin = "16px auto"; // Center align with margin
        img.style.borderRadius = "8px";
        img.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
      });

      // Find all videos in the content
      const videos = tempDiv.querySelectorAll("video");
      videos.forEach((video) => {
        // Set responsive video styles
        video.style.maxWidth = "100%";
        video.style.height = "auto";
        video.style.display = "block";
        video.style.margin = "16px auto"; // Center align with margin
        video.style.borderRadius = "8px";
        video.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
        video.setAttribute("controls", "true"); // Ensure controls are visible
      });

      // Find all iframes (for embedded videos like YouTube)
      const iframes = tempDiv.querySelectorAll("iframe");
      iframes.forEach((iframe) => {
        // Create responsive wrapper for iframes
        const wrapper = document.createElement("div");
        wrapper.style.position = "relative";
        wrapper.style.paddingBottom = "56.25%"; // 16:9 aspect ratio
        wrapper.style.height = "0";
        wrapper.style.overflow = "hidden";
        wrapper.style.margin = "16px auto";
        wrapper.style.borderRadius = "8px";
        wrapper.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";

        iframe.style.position = "absolute";
        iframe.style.top = "0";
        iframe.style.left = "0";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.border = "none";

        // Wrap the iframe
        iframe.parentNode.insertBefore(wrapper, iframe);
        wrapper.appendChild(iframe);
      });

      setProcessedContent(tempDiv.innerHTML);
    }
  }, [content]);

  // Function to handle file click (for images/videos)
  const handleFileClick = (file) => {
    if (
      file.contentType.startsWith("image/") ||
      file.contentType.startsWith("video/")
    ) {
      setSelectedFile(file);
    } else {
      window.open(file.url + `?${IMAGE_URL_AZURE}`, "_blank");
    }
  };

  return (
    <Container maxWidth="lg">
      <Box
        display={"flex"}
        justifyContent={"center"}
        sx={{ textAlign: "center", marginBottom: 4 }}
      >
        <Button
          text="Back"
          backgroundColor="white"
          onClick={() => setStep(step - 1)}
        />
        <Button
          text={isEdit ? "Edit Content" : "Submit for approval"}
          onClick={() => {
            if (scheduledDate) {
              handleAddContent({ publish: false });
            }
          }}
          disabled={isLoadAddContent}
          isLoading={isLoadAddContent && !publish}
        />
        {(roles.Admin === role || roles.Editor === role) &&
          (!isEdit || (isEdit && status === "Pending")) &&
          roles.ContentCreator !== role && (
            <Button
              text="Publish"
              onClick={() => {
                if (scheduledDate) {
                  handleAddContent({ publish: true });
                }
              }}
              disabled={isLoadAddContent}
              isLoading={isLoadAddContent && publish}
            />
          )}
      </Box>

      <Box sx={{ padding: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={12}>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", marginBottom: 2 }}
            >
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              sx={{ marginBottom: 2 }}
            >
              {new Date(scheduledDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    width: "100%",
                    height: "auto",
                    overflow: "hidden",
                    borderRadius: 2,
                  }}
                >
                  {modal ? (
                    <img
                      src={image}
                      alt="Preview"
                      style={{ width: "100%", height: "auto" }}
                    />
                  ) : isEdit && !image.fileData ? (
                    <img
                      src={image.url + `?${IMAGE_URL_AZURE}`}
                      alt="Preview"
                      style={{ width: "100%", height: "auto" }}
                    />
                  ) : image?.contentType ? (
                    <CardMedia
                      component="img"
                      image={`data:${image?.contentType};base64,${image?.fileData}`}
                    />
                  ) : (
                    <img
                      src={
                        "https://static.thenounproject.com/png/1765551-200.png"
                      }
                      alt="Preview"
                      style={{ width: "100%", height: "auto" }}
                    />
                  )}
                </Box>
                <Box sx={{ marginTop: 2 }}>
                  {topics.map((el, index) => (
                    <Chip
                      key={index}
                      label={el.title}
                      sx={{
                        backgroundColor: primaryColor,
                        color: "black",
                        marginRight: 1,
                        marginBottom: 1,
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ marginBottom: 3 }}>
                  <Box
                    sx={{
                      display: "-webkit-box",
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        margin: "16px auto",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "& video": {
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        margin: "16px auto",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "& iframe": {
                        maxWidth: "100%",
                        margin: "16px auto",
                        display: "block",
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: processedContent }}
                    />
                  </Box>
                </Typography>
                {files && files.length > 0 && (
                  <Box className="mt-4">
                    <Typography
                      variant="subtitle2"
                      className="text-gray-600 mb-2"
                    >
                      Related Files:
                    </Typography>
                    <ArticleResources
                      files={files}
                      onFileClick={handleFileClick}
                      primaryColor={primaryColor}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ContentPreview;
